<template>
  <div class="pivot-widget">
    <div class="widget-header">
      <h3 v-if="title">{{ title }}</h3>
      <button class="export-btn" @click="exportToExcel">Export XLSX</button>
    </div>
    <div class="table-scroll">
      <table class="pivot-table">
        <thead>
          <tr>
            <th class="col-name">{{ rowLabel }}</th>
            <th class="col-value">{{ valueLabel }}</th>
            <th class="col-percent">% {{ valueLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groups" :key="group.name">
            <tr class="group-row" @click="toggle(group.name)">
              <td class="col-name">
                <span class="toggle-icon">{{ expanded[group.name] ? '−' : '+' }}</span>
                <strong>{{ group.name }}</strong>
              </td>
              <td class="col-value"><strong>{{ formatNum(group.total) }}</strong></td>
              <td class="col-percent"><strong>{{ formatPct(group.total / grandTotal) }}</strong></td>
            </tr>
            <template v-if="expanded[group.name]">
              <tr v-for="row in group.rows" :key="group.name + '-' + row.name" class="child-row">
                <td class="col-name child-name">{{ row.name }}</td>
                <td class="col-value">{{ formatNum(row.value) }}</td>
                <td class="col-percent">{{ formatPct(row.value / grandTotal) }}</td>
              </tr>
            </template>
          </template>
          <tr class="total-row">
            <td class="col-name"><strong>Total</strong></td>
            <td class="col-value"><strong>{{ formatNum(grandTotal) }}</strong></td>
            <td class="col-percent"><strong>100,00%</strong></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import * as XLSX from 'xlsx';

const props = defineProps({
  title: { type: String, default: '' },
  data: { type: Array, required: true },
  rowField: { type: String, required: true },
  colField: { type: String, required: true },
  valueField: { type: String, default: 'count' },
  rowLabel: { type: String, default: 'Grup' },
  valueLabel: { type: String, default: 'Valor' }
});

const expanded = ref({});

const groups = computed(() => {
  const map = new Map();
  (props.data || []).forEach(item => {
    const groupName = String(item[props.rowField] ?? 'N/A');
    const childName = String(item[props.colField] ?? 'N/A');
    const value = Number(item[props.valueField]) || 0;
    if (!map.has(groupName)) map.set(groupName, { name: groupName, total: 0, rows: [] });
    const g = map.get(groupName);
    g.total += value;
    g.rows.push({ name: childName, value });
  });
  const result = Array.from(map.values());
  result.forEach(g => g.rows.sort((a, b) => b.value - a.value));
  return result.sort((a, b) => b.total - a.total);
});

const grandTotal = computed(() => groups.value.reduce((sum, g) => sum + g.total, 0));

function toggle(name) {
  expanded.value[name] = !expanded.value[name];
}

function formatNum(val) {
  return Number(val || 0).toLocaleString('es-ES');
}

function formatPct(val) {
  if (!val || isNaN(val)) return '0,00%';
  return (val * 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

function exportToExcel() {
  const rows = [];
  groups.value.forEach(g => {
    g.rows.forEach(r => {
      rows.push({
        [props.rowLabel]: g.name,
        [props.valueLabel + ' detall']: r.name,
        [props.valueLabel]: r.value
      });
    });
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pivot');
  const safe = (props.title || 'pivot').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  XLSX.writeFile(wb, `${safe}.xlsx`);
}
</script>

<style scoped>
.pivot-widget {
  background: white;
  border-radius: 4px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.widget-header h3 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: 'Source Sans Pro', sans-serif;
}

.export-btn {
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #059669;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.export-btn:hover { background-color: #059669; border-color: #047857; }

.table-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.pivot-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.pivot-table th {
  text-align: left;
  padding: 10px 8px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.pivot-table td {
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.col-name { width: 50%; }
.col-value, .col-percent { width: 25%; text-align: right; }

.group-row {
  cursor: pointer;
  background-color: #fafafa;
}

.group-row:hover {
  background-color: #f0f0f0;
}

.toggle-icon {
  display: inline-block;
  width: 16px;
  margin-right: 6px;
  color: #6b7280;
  font-weight: bold;
  text-align: center;
}

.child-row .child-name {
  padding-left: 30px;
  color: #4b5563;
}

.total-row {
  background-color: #eff6ff;
  border-top: 2px solid #0b5394;
}

.total-row td {
  color: #0b5394;
  padding: 12px 8px;
}
</style>
